import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import "./_rightIconStrip.scss";

const STRIP_W = 64;
const ITEM_PAD = 6;

function useDarkTooltip(disabled = false) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  // A disabled button doesn't reliably fire mouseleave, so a hover that was
  // live when the button got disabled (e.g. tool went busy) would leave a
  // stale pos behind and the bubble would pop back once it re-enabled, with
  // the cursor long gone. Drop the hover whenever the button disables.
  useEffect(() => {
    if (disabled) setPos(null);
  }, [disabled]);
  const onMouseEnter = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setPos({ top: rect.top + rect.height / 2, right: window.innerWidth - rect.left + 8 });
  };
  const onMouseLeave = () => setPos(null);
  return { ref, pos, onMouseEnter, onMouseLeave };
}

// Portalled to <body>: the icon strips live inside z-indexed cards (e.g.
// #toolsPanelDrawer, zIndex 102) that form their own stacking context, so an
// in-place bubble sat under the body-level tool popover (zIndex 160) and the
// icon's name was unreadable while a tool was open.
function DarkTooltipBubble({ label, pos }) {
  if (!pos || !label) return null;
  return createPortal(
    <div
      style={{
        position: "fixed",
        top: pos.top,
        right: pos.right,
        transform: "translateY(-50%)",
        background: "#1e293b",
        color: "#fff",
        fontSize: 12,
        fontWeight: 500,
        fontFamily: '"Inter", sans-serif',
        padding: "5px 10px",
        borderRadius: 7,
        pointerEvents: "none",
        whiteSpace: "nowrap",
        zIndex: 9999,
        boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
      }}
    >
      {label}
      <span
        style={{
          position: "absolute",
          right: -5,
          top: "50%",
          transform: "translateY(-50%)",
          width: 0,
          height: 0,
          borderTop: "5px solid transparent",
          borderBottom: "5px solid transparent",
          borderLeft: "5px solid #1e293b",
        }}
      />
    </div>,
    document.body
  );
}

export const SceneIconButton = ({ label, onClick, disabled, children }) => {
  const { ref, pos, onMouseEnter, onMouseLeave } = useDarkTooltip(disabled);
  return (
    <>
      <button
        ref={ref}
        disabled={disabled}
        // Icon-only: the label only exists as a hover bubble, so it has to be
        // the accessible name too.
        aria-label={label}
        onClick={disabled ? undefined : onClick}
        onMouseEnter={disabled ? undefined : onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{
          background: "none",
          border: "none",
          padding: "7px",
          borderRadius: "8px",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.3 : 1,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "background 0.12s",
          flexShrink: 0,
        }}
      >
        {children}
      </button>
      {!disabled && <DarkTooltipBubble label={label} pos={pos} />}
    </>
  );
};

const ActionButton = ({ Icon, label, onClick, danger, active }) => {
  const { ref, pos, onMouseEnter, onMouseLeave } = useDarkTooltip();

  const restBackground = active ? "rgba(169,99,245,0.12)" : "transparent";
  const accent = active ? "#a963f5" : danger ? "#ef4444" : "#6b7280";

  const handleMouseEnter = (e) => {
    e.currentTarget.style.background = danger ? "rgba(239,68,68,0.08)" : "rgba(0,0,0,0.05)";
    onMouseEnter();
  };
  const handleMouseLeave = (e) => {
    e.currentTarget.style.background = restBackground;
    onMouseLeave();
  };

  return (
    <>
      <button
        ref={ref}
        onClick={onClick}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          padding: "6px 0 5px",
          border: "none",
          borderRadius: 8,
          background: restBackground,
          cursor: "pointer",
          alignSelf: "stretch",
          boxSizing: "border-box",
          transition: "background 0.15s",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 20,
            height: 20,
            flexShrink: 0,
            color: accent,
          }}
        >
          <Icon style={{ width: 17, height: 17 }} />
        </span>
        <span
          style={{
            fontSize: 10,
            color: accent,
            fontWeight: active ? 600 : 400,
            lineHeight: "13px",
            fontFamily: '"Inter", sans-serif',
            textAlign: "center",
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            paddingInline: 3,
          }}
        >
          {label}
        </span>
      </button>

      <DarkTooltipBubble label={label} pos={pos} />
    </>
  );
};

export default function RightIconStrip({
  tabs,
  activeTab,
  onTabChange,
  top,
  right,
  actions,
  opacity,
  onMouseEnter,
  onMouseLeave,
}) {
  return (
    <div
      className="rightIconStrip"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: "absolute",
        right: right ?? 16,
        top: top ?? 16,
        zIndex: 103,
        background: "white",
        borderRadius: 14,
        opacity: opacity ?? 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        padding: `6px ${ITEM_PAD}px`,
        width: STRIP_W,
        maxHeight: "calc(100vh - 120px)",
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "none",
      }}
    >
      {/* Panel tab buttons */}
      {tabs.map(({ key, Icon, label, badge }) => {
        const active = activeTab === key;
        return (
          <button
            key={key}
            onClick={() => onTabChange(key)}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              padding: "8px 0 7px",
              border: "none",
              borderRadius: 10,
              background: active ? "rgba(169,99,245,0.12)" : "transparent",
              cursor: "pointer",
              alignSelf: "stretch",
              position: "relative",
              transition: "background 0.15s",
              boxSizing: "border-box",
            }}
          >
            <span
              className={key === "merlin" ? "merlin-icon-animated" : undefined}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 22,
                height: 22,
                flexShrink: 0,
                color: active ? "#a963f5" : "#6b7280",
              }}
            >
              <Icon style={{ width: 20, height: 20 }} />
            </span>
            <span
              style={{
                fontSize: 10,
                color: active ? "#a963f5" : "#6b7280",
                fontWeight: active ? 600 : 400,
                lineHeight: "13px",
                fontFamily: '"Inter", sans-serif',
                textAlign: "center",
                width: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                paddingInline: 2,
              }}
            >
              {label}
            </span>
            {badge && (
              <span
                style={{
                  position: "absolute",
                  top: 7,
                  right: 7,
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#a963f5",
                }}
              />
            )}
          </button>
        );
      })}

      {/* Element action buttons — shown when a box is selected */}
      {actions && actions.length > 0 && (
        <>
          <div style={{ height: 1, background: "#f1f5f9", margin: "4px 8px" }} />
          {actions.map(({ key, Icon, label, onClick, danger, active }) => (
            <ActionButton
              key={key}
              Icon={Icon}
              label={label}
              onClick={onClick}
              danger={danger}
              active={active}
            />
          ))}
        </>
      )}
    </div>
  );
}
